/**
 * useConnectionMonitor — détecte les pertes de connexion (réseau ou backend
 * down / cold-start Cloud Run) et expose un état à afficher dans un toast.
 *
 * Triggers :
 *   1. `navigator.onLine` passe à false (réseau OS down)
 *   2. Un fetch vers le backend a échoué (la dernière X requêtes ont timeout
 *      / 5XX) — détecté via un event global `labelforge:fetch-failed`
 *   3. Polling périodique vers /health (toutes les 30s en mode normal, toutes
 *      les 5s en mode dégradé pour détecter le retour)
 *
 * Stratégie de retry exponentielle (utilisée par l'UI pour communiquer) :
 *   - 1ère tentative immédiate
 *   - 2ème après 2s
 *   - 3ème après 5s
 *   - 4ème+ après 10s, 20s, 30s (max 30s)
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { API_URL } from '../api/client';


// /api/version : route publique, sans authentification ni effet de bord
// (simple lecture d'une variable d'environnement). L'ancien '/health'
// n'existe pas côté backend et renvoyait un 404 toutes les 30 s dans la
// console — le code s'en contentait, mais c'était du bruit permanent.
const HEALTH_PATH = '/api/version';
const POLL_NORMAL_MS = 30_000;   // 30s en mode connecté
const POLL_DEGRADED_MS = 5_000;  // 5s tant qu'on est en panne
const REQUEST_TIMEOUT_MS = 6_000;


async function pingBackend(signal) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
        const res = await fetch(`${API_URL}${HEALTH_PATH}`, {
            method: 'GET',
            signal: signal || controller.signal,
            cache: 'no-store',
            credentials: 'omit',
        });
        clearTimeout(timer);
        // 200 OK ou 404 (endpoint absent mais serveur répond) = en ligne.
        // 5XX ou network = hors ligne.
        return res.status < 500;
    } catch (_) {
        clearTimeout(timer);
        return false;
    }
}


export function useConnectionMonitor() {
    // 'online' | 'degraded' | 'offline'
    //   online   : tout va bien, polling à 30s
    //   degraded : on a vu un échec récent, on poll à 5s, on affiche un toast
    //   offline  : navigator.onLine = false (le système d'exploitation confirme)
    const [status, setStatus] = useState('online');
    const [retryCount, setRetryCount] = useState(0);
    const [lastFailureAt, setLastFailureAt] = useState(null);
    const pollTimerRef = useRef(null);
    const inFlightRef = useRef(null);

    const doPoll = useCallback(async () => {
        // Évite les requêtes concurrentes
        if (inFlightRef.current) return;
        inFlightRef.current = pingBackend();
        const ok = await inFlightRef.current;
        inFlightRef.current = null;

        if (ok) {
            // Si on était en panne, on revient en mode normal
            setStatus((prev) => {
                if (prev === 'online') return prev;
                setRetryCount(0);
                setLastFailureAt(null);
                return 'online';
            });
        } else {
            setStatus((prev) => prev === 'offline' ? prev : 'degraded');
            setRetryCount((c) => c + 1);
            setLastFailureAt((t) => t || Date.now());
        }
    }, []);

    // Force une vérification immédiate (utilisé par le toast pour le bouton "Réessayer")
    const forceCheck = useCallback(() => {
        doPoll();
    }, [doPoll]);

    // Lifecycle : navigator online/offline + polling
    useEffect(() => {
        const handleOnline = () => {
            setStatus('degraded'); // on repasse en degraded en attendant la confirmation du backend
            doPoll();
        };
        const handleOffline = () => {
            setStatus('offline');
        };
        const handleFetchFailed = () => {
            setStatus((prev) => prev === 'online' ? 'degraded' : prev);
            setLastFailureAt((t) => t || Date.now());
            // Trigger un poll dans la foulée pour confirmer / clarifier
            doPoll();
        };

        // État initial : si OS dit offline, on est offline
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            setStatus('offline');
        }

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('labelforge:fetch-failed', handleFetchFailed);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('labelforge:fetch-failed', handleFetchFailed);
        };
    }, [doPoll]);

    // Polling périodique avec interval adapté au statut
    useEffect(() => {
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        if (status === 'offline') {
            // Pas de polling tant que l'OS dit offline (économie batterie)
            return;
        }
        const interval = status === 'online' ? POLL_NORMAL_MS : POLL_DEGRADED_MS;
        pollTimerRef.current = setTimeout(() => doPoll(), interval);
        return () => {
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
    }, [status, retryCount, doPoll]);

    return { status, retryCount, lastFailureAt, forceCheck };
}
